import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, AlertTriangle } from 'lucide-react';
import { offersApi, startPointsApi, usersApi, patientsApi, Offer, OfferTransportMode, Patient as FullPatient } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay';
import PatientDetailModal from '../../../components/PatientDetailModal/PatientDetailModal';
import CreateExitHubModal from '../../../components/CreateExitHubModal/CreateExitHubModal';
import { useScrollToError } from '../../../hooks/useScrollToError';
import './OfferConfigurationStep.css';

interface NationOption {
  code: string;
  name: string;
  flagImage: string;
  hasUser: boolean;
}

interface Patient {
  id: string;
  patientId?: string;
  medicalPriority: string;
  nationality?: string;
  patientGender: string;
  patientAge: number;
  medicalPrimaryConditionDiagnosis: string;
}

interface ExitPoint {
  id: string;
  name: string;
  nation: string;
}

type TransportIntent = 'full' | 'ext_transport' | 'treatment_only' | 'provide_transport';

interface OfferConfigurationStepProps {
  requestId: string;
  requestCategory: string;
  requestExitPoint?: ExitPoint;
  shortlistedPatients: Patient[];
  selectedOfferType: { [patientId: string]: 'TREATMENT_AND_TRANSPORT' | 'TREATMENT_ONLY' };
  transportIntent: { [patientId: string]: TransportIntent };
  existingOfferId?: string;
  existingReceivingStartPointId?: string;
  existingComment?: string;
  existingTransportMode?: OfferTransportMode;
  existingTransportProviderNationId?: string;
  existingTransportAgreementNote?: string;
  onBack: () => void;
  onSubmitted: () => void;
}

interface StartPoint {
  id: string;
  name: string;
  nation?: string;
  types: string[];
}

// Treatment nation info derived from other nations' offers
interface TreatmentNationInfo {
  nation: string;
  flagImage?: string;
  exitHubName?: string;
  patientIds: string[];
  selected: boolean;
}

function OfferConfigurationStep({
  requestId,
  requestCategory,
  requestExitPoint,
  shortlistedPatients,
  selectedOfferType,
  transportIntent,
  existingOfferId,
  existingReceivingStartPointId,
  existingComment,
  existingTransportMode,
  existingTransportProviderNationId,
  existingTransportAgreementNote,
  onBack,
  onSubmitted
}: OfferConfigurationStepProps) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [receivingStartPointId, setReceivingStartPointId] = useState(existingReceivingStartPointId || '');
  const [comment, setComment] = useState(existingComment || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useScrollToError(error);

  // Per-group transport mode state (for ext_transport group)
  const [transportProviderNationIdTO, setTransportProviderNationIdTO] = useState(existingTransportProviderNationId || '');
  const [transportAgreementNoteTO, setTransportAgreementNoteTO] = useState(existingTransportAgreementNote || '');

  const [startPoints, setStartPoints] = useState<StartPoint[]>([]);
  const [loadingStartPoints, setLoadingStartPoints] = useState(false);
  const [availableNations, setAvailableNations] = useState<NationOption[]>([]);
  const [detailPatient, setDetailPatient] = useState<FullPatient | null>(null);

  const [showCreateHub, setShowCreateHub] = useState(false);

  // Provide Transport: other nations' offers and selection state
  const [treatmentNations, setTreatmentNations] = useState<TreatmentNationInfo[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [transportMessage, setTransportMessage] = useState('');

  // Group patients by transport intent
  const groups = useMemo(() => {
    const full: Patient[] = [];
    const extTransport: Patient[] = [];
    const treatmentOnly: Patient[] = [];
    const provideTransport: Patient[] = [];

    shortlistedPatients.forEach(p => {
      const intent = transportIntent[p.id];
      if (intent === 'provide_transport') {
        provideTransport.push(p);
      } else if (selectedOfferType[p.id] === 'TREATMENT_AND_TRANSPORT') {
        full.push(p);
      } else if (intent === 'ext_transport') {
        extTransport.push(p);
      } else {
        treatmentOnly.push(p);
      }
    });

    return { full, extTransport, treatmentOnly, provideTransport };
  }, [shortlistedPatients, selectedOfferType, transportIntent]);

  // Real offer patients (excluding provide_transport which is notification-only)
  const realOfferPatients = useMemo(() =>
    shortlistedPatients.filter(p => transportIntent[p.id] !== 'provide_transport'),
    [shortlistedPatients, transportIntent]
  );

  const isTransportRequest = requestCategory === 'TRANSPORT';

  // Determine the effective offer type for the payload
  const effectiveOfferType = useMemo(() => {
    if (isTransportRequest) return 'TRANSPORT_EXTENSION';
    if (groups.full.length > 0) return 'TREATMENT_AND_TRANSPORT';
    return 'TREATMENT_ONLY';
  }, [groups.full.length, isTransportRequest]);

  useEffect(() => {
    loadStartPoints();
    loadNations();
  }, []);

  // Load other nations' offers for provide_transport container
  useEffect(() => {
    if (groups.provideTransport.length > 0) {
      loadTreatmentNationOffers();
    }
  }, [groups.provideTransport.length]);

  const loadStartPoints = async () => {
    if (!user?.nation) return;
    try {
      setLoadingStartPoints(true);
      const response = await startPointsApi.getAll({ nation: user.nation, isActive: true });
      setStartPoints(response.data);
    } catch (err) {
      console.error('Failed to load start points:', err);
    } finally {
      setLoadingStartPoints(false);
    }
  };

  const loadNations = async () => {
    try {
      const response = await usersApi.getNations();
      const nations = (response.data as NationOption[]).filter(n => n.name !== user?.nation);
      setAvailableNations(nations);
    } catch (err) {
      console.error('Failed to load nations:', err);
    }
  };

  const loadTreatmentNationOffers = async () => {
    try {
      setLoadingOffers(true);
      const response = await offersApi.getByRequest(requestId);
      const allOffers: Offer[] = response.data;

      // Find which nations have treatment-only offers for our provide_transport patients
      const provideTransportPatientIds = new Set(groups.provideTransport.map(p => p.id));
      const nationMap = new Map<string, TreatmentNationInfo>();

      allOffers.forEach(offer => {
        if (!offer.user?.nation || offer.user.nation === user?.nation) return;

        const matchingPatientIds = (offer.offerPatients || [])
          .filter(op => provideTransportPatientIds.has(op.patientId))
          .map(op => op.patientId);

        if (matchingPatientIds.length > 0) {
          const existing = nationMap.get(offer.user.nation);
          if (existing) {
            matchingPatientIds.forEach(id => {
              if (!existing.patientIds.includes(id)) existing.patientIds.push(id);
            });
          } else {
            nationMap.set(offer.user.nation, {
              nation: offer.user.nation,
              flagImage: offer.user.flagImage,
              exitHubName: offer.receivingStartPoint?.name,
              patientIds: matchingPatientIds,
              selected: true
            });
          }
        }
      });

      setTreatmentNations(Array.from(nationMap.values()));
    } catch (err) {
      console.error('Failed to load treatment nation offers:', err);
    } finally {
      setLoadingOffers(false);
    }
  };

  const toggleTreatmentNation = (nation: string) => {
    setTreatmentNations(prev =>
      prev.map(tn => tn.nation === nation ? { ...tn, selected: !tn.selected } : tn)
    );
  };

  const selectedTreatmentNations = useMemo(() =>
    treatmentNations.filter(tn => tn.selected),
    [treatmentNations]
  );

  const handlePatientClick = async (patientId: string) => {
    try {
      const response = await patientsApi.getById(patientId);
      setDetailPatient(response.data);
    } catch (err) {
      console.error('Failed to load patient details:', err);
    }
  };

  const handleSubmit = async () => {
    if (!isTransportRequest && realOfferPatients.length > 0 && !receivingStartPointId) {
      setError(t('requests.selectExitHub'));
      return;
    }

    if (groups.extTransport.length > 0 && !transportProviderNationIdTO) {
      setError(t('requests.fieldRequired'));
      return;
    }

    // Validate provide_transport: at least one nation must be selected (not for transport-only requests)
    if (!isTransportRequest && groups.provideTransport.length > 0 && selectedTreatmentNations.length === 0) {
      setError(t('requests.fieldRequired'));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Build per-patient offers
      {
        const patientOffers = realOfferPatients.map(p => {
          const pOfferType = selectedOfferType[p.id] || 'TREATMENT_ONLY' as const;
          const intent = transportIntent[p.id];
          const isTT = pOfferType === 'TREATMENT_AND_TRANSPORT';

          let transportMode: OfferTransportMode;
          if (isTT) {
            transportMode = 'INCLUDED_BY_OFFERING_NATION';
          } else if (intent === 'ext_transport') {
            transportMode = 'PROVIDED_BY_PARTNER_NATION';
          } else {
            transportMode = 'OPEN';
          }

          return {
            patientId: p.id,
            offerType: pOfferType,
            transportMode,
            transportProviderNationId: intent === 'ext_transport' ? transportProviderNationIdTO || undefined : undefined,
            transportAgreementNote: intent === 'ext_transport' ? transportAgreementNoteTO || undefined : undefined,
          };
        });

        // Also include provide_transport patients as TRANSPORT_EXTENSION for persistence
        groups.provideTransport.forEach(p => {
          patientOffers.push({
            patientId: p.id,
            offerType: 'TRANSPORT_EXTENSION' as any,
            transportMode: 'INCLUDED_BY_OFFERING_NATION',
            transportProviderNationId: undefined,
            transportAgreementNote: undefined,
          });
        });

        const globalTransportMode = groups.extTransport.length > 0
          ? 'PROVIDED_BY_PARTNER_NATION'
          : groups.full.length > 0
            ? 'INCLUDED_BY_OFFERING_NATION'
            : 'OPEN';

        const payload: any = {
          offerType: effectiveOfferType,
          patientOffers,
          receivingStartPointId: receivingStartPointId || undefined,
          comment: comment || undefined,
          transportMode: globalTransportMode,
          transportProviderNationId: globalTransportMode === 'PROVIDED_BY_PARTNER_NATION' ? transportProviderNationIdTO || undefined : undefined,
          transportAgreementNote: globalTransportMode === 'PROVIDED_BY_PARTNER_NATION' ? transportAgreementNoteTO || undefined : undefined,
        };

        if (existingOfferId) {
          await offersApi.update(existingOfferId, payload);
        } else {
          await offersApi.create({ requestId, ...payload });
        }
      }

      // Send transport intent notifications for provide_transport patients
      if (groups.provideTransport.length > 0 && selectedTreatmentNations.length > 0) {
        await offersApi.sendTransportIntent({
          requestId,
          patientIds: groups.provideTransport.map(p => p.id),
          targetNations: selectedTreatmentNations.map(tn => tn.nation),
          message: transportMessage || undefined,
        });
      }

      setSubmitting(false);
      onSubmitted();
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToSave'));
      setSubmitting(false);
    }
  };

  return (
    <div className="offer-configuration-step" style={{ position: 'relative' }}>
      <LoadingOverlay loading={submitting} text={`${t('offers.submitting')}`} />
      {/* === STICKY TOOLBAR === */}
      <div className="config-toolbar">
        <span className="step-label">{t('requests.step', { number: 2 })}: {t('offers.offerConfiguration')}</span>

        <span className="config-summary">
          {shortlistedPatients.length} {t('patients.title').toLowerCase()}
          {!isTransportRequest && groups.full.length > 0 && <span className="summary-tag both">{t('offers.fullOffer')}: {groups.full.length}</span>}
          {!isTransportRequest && groups.extTransport.length > 0 && <span className="summary-tag ext-transport">{t('offers.treatment')} + Ext. {t('offers.transport')}: {groups.extTransport.length}</span>}
          {!isTransportRequest && groups.treatmentOnly.length > 0 && <span className="summary-tag treatment">{t('offers.treatmentOffer')}: {groups.treatmentOnly.length}</span>}
          {groups.provideTransport.length > 0 && <span className="summary-tag provide-transport">{isTransportRequest ? t('offers.transport') : t('offers.transportOffer')}: {groups.provideTransport.length}</span>}
        </span>

        <div className="config-toolbar-actions">
          <button className="btn btn-secondary" onClick={onBack} disabled={submitting}>
            <ArrowLeft size={14} /> {t('common.back')}
          </button>
          <button
            className="btn btn-primary btn-submit"
            onClick={handleSubmit}
            disabled={submitting || shortlistedPatients.length === 0}
          >
            {submitting ? t('offers.submitting') : existingOfferId ? `${t('common.update')} (${shortlistedPatients.length})` : `${t('offers.submitOffer')} (${shortlistedPatients.length})`}
          </button>
        </div>
      </div>

      {/* === SCROLLABLE CONTENT === */}
      <div className="config-scroll-area">
        {error && (
          <div ref={errorRef} className="error-message">
            <strong>{t('common.error')}:</strong> {error}
          </div>
        )}

        {/* Global: UCPM Exit Hub + Transit Hub */}
        {/* Show exit hub selector only when offering treatment (not for transport-only or provide_transport-only) */}
        {!isTransportRequest && realOfferPatients.length > 0 && (
          <div className="configuration-form">
            {requestExitPoint && (
              <div className="form-group">
                <label>{t('hubs.transitHub')}</label>
                <div className="readonly-field">
                  {requestExitPoint.name} ({requestExitPoint.nation})
                </div>
                <small>{t('hubs.transitHub')}</small>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="receivingStartPoint">
                {t('hubs.exitHub')} <span className="required">*</span>
              </label>
              <div className="exit-hub-row">
                <div className="exit-hub-select">
                  {loadingStartPoints ? (
                    <div className="loading-small">{t('common.loading')}</div>
                  ) : startPoints.length === 0 ? (
                    <div className="no-hubs-warning">
                      <AlertTriangle size={16} />
                      <span>{t('hubs.noHubs')}</span>
                    </div>
                  ) : (
                    <select
                      id="receivingStartPoint"
                      value={receivingStartPointId}
                      onChange={(e) => setReceivingStartPointId(e.target.value)}
                      required
                      disabled={submitting}
                    >
                      <option value="">{t('requests.selectExitHub')}...</option>
                      {startPoints.map(sp => (
                        <option key={sp.id} value={sp.id}>
                          {sp.name} ({sp.nation})
                        </option>
                      ))}
                    </select>
                  )}
                  <small>{t('requests.selectExitHub')}</small>
                </div>
                {!loadingStartPoints && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-create-hub"
                    onClick={() => setShowCreateHub(true)}
                    disabled={submitting}
                  >
                    <Plus size={14} /> {t('requests.createExitHub')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Read-only transit hub info for provide-transport-only offers */}
        {!isTransportRequest && realOfferPatients.length === 0 && groups.provideTransport.length > 0 && (
          <div className="configuration-form">
            {requestExitPoint && (
              <div className="form-group">
                <label>{t('hubs.transitHub')}</label>
                <div className="readonly-field">
                  {requestExitPoint.name} ({requestExitPoint.nation})
                </div>
                <small>{t('hubs.transitHub')}</small>
              </div>
            )}
          </div>
        )}

        {isTransportRequest && (
          <div className="configuration-form">
            <div className="transport-request-info" style={{ padding: '12px', background: '#e3f2fd', borderRadius: '6px', marginBottom: '12px', fontSize: '0.9rem' }}>
              {t('offers.transportOffer')}
            </div>
            {requestExitPoint && (
              <div className="form-group">
                <label>{t('common.from')}</label>
                <div className="readonly-field">{requestExitPoint.name} ({requestExitPoint.nation})</div>
              </div>
            )}
          </div>
        )}

        {/* === Offer Type Containers === */}
        <div className="offer-containers">

          {/* Full Offer Container */}
          {groups.full.length > 0 && (
            <div className="offer-container offer-container-full">
              <div className="offer-container-header">
                <span className="offer-container-icon">
                  <img src="/icons/offer_combined_gruen.webp" alt="" width={42} height={24} />
                </span>
                <h3>{t('offers.fullOffer')}</h3>
                <span className="offer-container-count">{groups.full.length} {t('patients.title').toLowerCase()}</span>
              </div>
              <div className="offer-container-info">
                {t('offers.combined')}
              </div>
              <div className="offer-container-patients">
                {groups.full.map(p => (
                  <span key={p.id} className="patient-chip patient-chip-clickable" onClick={() => handlePatientClick(p.id)}>{p.patientId}</span>
                ))}
              </div>
            </div>
          )}

          {/* Treatment + Ext. Transport Container */}
          {groups.extTransport.length > 0 && (
            <div className="offer-container offer-container-ext-transport">
              <div className="offer-container-header">
                <span className="offer-container-icon">
                  <img src="/icons/delivery_gelb.webp" alt="" width={24} height={24} />
                  <img src="/icons/hospital_gruen.webp" alt="" width={24} height={24} />
                </span>
                <h3>{t('offers.treatment')} + Ext. {t('offers.transport')}</h3>
                <span className="offer-container-count">{groups.extTransport.length} {t('patients.title').toLowerCase()}</span>
              </div>
              <div className="offer-container-info">
                {t('offers.transportExtension')}
              </div>
              <div className="offer-container-patients">
                {groups.extTransport.map(p => (
                  <span key={p.id} className="patient-chip patient-chip-clickable" onClick={() => handlePatientClick(p.id)}>{p.patientId}</span>
                ))}
              </div>
              <div className="offer-container-fields">
                <div className="form-group">
                  <label htmlFor="partnerNation">
                    {t('common.nation')} <span className="required">*</span>
                  </label>
                  <select
                    id="partnerNation"
                    value={transportProviderNationIdTO}
                    onChange={(e) => setTransportProviderNationIdTO(e.target.value)}
                    disabled={submitting}
                  >
                    <option value="">{t('users.selectNation')}...</option>
                    {availableNations.map(nation => (
                      <option key={nation.code} value={nation.name}>
                        {nation.name} ({nation.code})
                      </option>
                    ))}
                  </select>
                  <small>{t('offers.transportExtension')}</small>
                </div>
                <div className="form-group">
                  <label htmlFor="transportNote">{t('common.notes')}</label>
                  <textarea
                    id="transportNote"
                    value={transportAgreementNoteTO}
                    onChange={(e) => setTransportAgreementNoteTO(e.target.value)}
                    placeholder="Additional context about the transport arrangement (optional)..."
                    rows={2}
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Treatment Only Container */}
          {groups.treatmentOnly.length > 0 && (
            <div className="offer-container offer-container-treatment-only">
              <div className="offer-container-header">
                <span className="offer-container-icon">
                  <img src="/icons/delivery_leer.webp" alt="" width={24} height={24} />
                  <img src="/icons/hospital_gruen.webp" alt="" width={24} height={24} />
                </span>
                <h3>{t('offers.treatmentOffer')}</h3>
                <span className="offer-container-count">{groups.treatmentOnly.length} {t('patients.title').toLowerCase()}</span>
              </div>
              <div className="offer-container-info">
                {t('offers.treatmentOffer')}
              </div>
              <div className="offer-container-patients">
                {groups.treatmentOnly.map(p => (
                  <span key={p.id} className="patient-chip patient-chip-clickable" onClick={() => handlePatientClick(p.id)}>{p.patientId}</span>
                ))}
              </div>
            </div>
          )}

          {/* Provide Transport Container */}
          {groups.provideTransport.length > 0 && (
            <div className="offer-container offer-container-provide-transport">
              <div className="offer-container-header">
                <span className="offer-container-icon">
                  <img src="/icons/delivery_gruen.webp" alt="" width={24} height={24} />
                  <img src="/icons/hospital_gelb.webp" alt="" width={24} height={24} />
                </span>
                <h3>{t('offers.transportOffer')}</h3>
                <span className="offer-container-count">{groups.provideTransport.length} {t('patients.title').toLowerCase()}</span>
              </div>
              <div className="offer-container-info">
                {t('offers.transportOffer')}
              </div>
              <div className="offer-container-patients">
                {groups.provideTransport.map(p => (
                  <span key={p.id} className="patient-chip patient-chip-clickable" onClick={() => handlePatientClick(p.id)}>{p.patientId}</span>
                ))}
              </div>

              {loadingOffers ? (
                <div className="loading-small">{t('common.loading')}</div>
              ) : treatmentNations.length === 0 ? (
                <div className="offer-container-warning">
                  {t('offers.noOffers')}
                </div>
              ) : (
                <div className="treatment-nation-list">
                  <label className="treatment-nation-label">{t('offers.treatment')}:</label>
                  {treatmentNations.map(tn => (
                    <label key={tn.nation} className={`treatment-nation-item ${tn.selected ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={tn.selected}
                        onChange={() => toggleTreatmentNation(tn.nation)}
                        disabled={submitting}
                      />
                      <span className="treatment-nation-info">
                        {tn.flagImage && (
                          <img src={tn.flagImage} alt={tn.nation} className="treatment-nation-flag" />
                        )}
                        <strong>{tn.nation}</strong>
                        {tn.exitHubName && <span className="treatment-nation-hub">{t('hubs.exitHub')}: {tn.exitHubName}</span>}
                        <span className="treatment-nation-patients">({tn.patientIds.length} {t('patients.title').toLowerCase()})</span>
                      </span>
                    </label>
                  ))}
                  {selectedTreatmentNations.length === 0 && (
                    <div className="offer-container-warning">
                      {t('common.noData')}
                    </div>
                  )}
                </div>
              )}

              <div className="offer-container-fields">
                <div className="form-group">
                  <label htmlFor="transportMessage">{t('common.notes')} ({t('common.optional')})</label>
                  <textarea
                    id="transportMessage"
                    value={transportMessage}
                    onChange={(e) => setTransportMessage(e.target.value)}
                    placeholder="Add a message to the treatment nations about your transport offer..."
                    rows={2}
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Additional Comments */}
        <div className="configuration-form">
          <div className="form-group">
            <label htmlFor="comment">{t('common.comments')}</label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add any additional information about your offer (optional)..."
              rows={4}
              disabled={submitting}
            />
          </div>
        </div>
      </div>

      {detailPatient && (
        <PatientDetailModal
          patient={detailPatient}
          onClose={() => setDetailPatient(null)}
        />
      )}

      {showCreateHub && (
        <CreateExitHubModal
          onClose={() => setShowCreateHub(false)}
          onCreated={async (newHub) => {
            await loadStartPoints();
            setReceivingStartPointId(newHub.id);
            setShowCreateHub(false);
          }}
        />
      )}
    </div>
  );
}

export default OfferConfigurationStep;
